<template>
  <div class="v-upload" :class="{ 'has-errors': errors.length, 'is-disabled': isDisabled }" tabindex="0" @click="onClick" @keydown="onKeydown">
    <VUploadDragger v-if="drag" :disabled="isDisabled" :optional @drop="onDrop">
      <slot />
    </VUploadDragger>

    <slot v-else>
      <VButton label="Upload" />
    </slot>

    <input ref="input" :accept class="v-upload__input" :multiple type="file" @change="onChange" />

    <VFileList :files @remove-file="removeFile">
      <template #file-list>
        <slot name="file-list" />
      </template>
    </VFileList>

    <VErrors :errors />
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onActivated, onDeactivated, useTemplateRef, watch } from 'vue'

import { useErrors } from '../../composables/useErrors.ts'
import { humanSize } from '../../utils/helpers.ts'
import { $formKey } from '../../utils/injections.ts'
import { $message } from '../Notification/index.ts'
import VButton from '../VButton.vue'
import VErrors from '../VErrors.vue'
import VFileList from './VFileList.vue'
import VUploadDragger from './VUploadDragger.vue'

interface Props {
  accept?: string
  disabled?: boolean
  drag?: boolean
  error?: string | string[]
  files?: File[]
  multiple?: boolean
  optional?: boolean
  pastable?: boolean

  /**
   * The maximum number of files.
   */
  max?: number

  onErrorSmall?: (size: number) => void
  onErrorLarge?: (size: number) => void

  /**
   * The minimum filesize in bytes.
   */
  minSize?: number

  /**
   * The maximum filesize in bytes.
   */
  maxSize?: number
}

const props = withDefaults(defineProps<Props>(), {
  error: '',
  multiple: false,
  drag: false,
  disabled: undefined,
  pastable: false,
  max: undefined,
  files: () => [],
  accept: undefined,
  minSize: undefined,
  maxSize: undefined,
  onErrorSmall: (size: number) => $message.error(`This file is too small. Min filesize is: ${humanSize(size)}`),
  onErrorLarge: (size: number) => $message.error(`This file is too large. Max filesize is: ${humanSize(size)}`),
})

const emit = defineEmits<{
  'update:files': [files: File[]]
}>()

defineSlots<{
  default?(): any
  'file-list'?(): any
}>()

const form = inject($formKey, undefined)

const errors = useErrors(props)

const input = useTemplateRef('input')

const isDisabled = computed(() => !!(props.disabled || (props.disabled === undefined && form?.props?.disabled)))

watch(
  () => props.pastable,
  () => {
    if (props.pastable) {
      document.addEventListener('paste', onPaste)
    } else {
      document.removeEventListener('paste', onPaste)
    }
  },
  { immediate: true },
)

onActivated(() => {
  if (props.pastable) {
    document.addEventListener('paste', onPaste)
  }
})
onDeactivated(() => document.removeEventListener('paste', onPaste))

function addFiles(files: File[]) {
  const filtered = filterFiles(files)

  if (filtered.length === 0) {
    return
  }

  const updateFiles = Array.from(props.files)

  for (const file of filtered) {
    if (!updateFiles.some((existingFile) => existingFile.size === file.size && existingFile.type === file.type && existingFile.name === file.name)) {
      updateFiles.push(file)
    }
  }

  emit('update:files', updateFiles)
}

function onPaste(event: ClipboardEvent) {
  if (props.pastable && event.clipboardData?.files) {
    addFiles(Array.from(event.clipboardData?.files))
  }
}

function onChange() {
  addFiles(Array.from(input.value?.files ?? []))
}

function onDrop(files: File[]) {
  addFiles(files)
}

function onClick() {
  if (!isDisabled.value && input.value) {
    input.value.value = ''
    input.value.click()
  }
}

function onKeydown(event: KeyboardEvent) {
  if (event.target !== event.currentTarget) return

  if (event.key === 'Enter' || event.key === 'Space') {
    onClick()
  }
}

function filterFiles(files: File[]) {
  return files
    .filter(({ type, name, size }) => {
      if (props.minSize && size < props.minSize) {
        props.onErrorSmall(props.minSize)

        return false
      }

      if (props.maxSize && size > props.maxSize) {
        props.onErrorLarge(props.maxSize)

        return false
      }

      if (!props.accept) {
        return true
      }

      const extension = name.indexOf('.') > -1 ? `.${name.split('.').pop()}` : ''

      return props.accept
        .split(',')
        .map((type) => type.trim())
        .filter((type) => type)
        .some((acceptedType) => {
          if (/\..+$/.test(acceptedType)) {
            return extension.toLowerCase() === acceptedType.toLowerCase()
          }

          if (/^[^/]+\/[^/]+$/.test(acceptedType)) {
            return type === acceptedType
          }

          return false
        })
    })
    .slice(0, props.max)
}

function removeFile(index: number) {
  const files = props.files.slice()
  files.splice(index, 1)
  emit('update:files', files)
}

defineExpose({
  filterFiles,
  input,
  onKeydown,
  onClick,
  onChange,
  onDrop,
  removeFile,
})
</script>

<style>
.v-upload {
  width: 100%;
  outline: none;
}

.v-upload__input {
  display: none;
}
</style>
